import React, { useState, useEffect, useRef } from 'react';
import SideToSideCatcherGame from '../games/SideToSideCatcherGame';
import DodgeSurvivalGame from '../games/DodgeSurvivalGame';
import LoadingGameSelector from './LoadingGameSelector';
import { saveGameScore } from '../../utils/miniGameStorage';
import './MiniGameLoadingOverlay.css';

/**
 * Mini-Game Loading Overlay Component
 * Two-phase UX: prompt mode → game mode
 * 
 * Props:
 * - loadStatus: 'loading' | 'ready' | 'error'
 * - contentType: 'flashcards' | 'quizzes' | 'podcasts' | 'audiobooks'
 * - onReady: () => void - called when user clicks "Ready" button
 * - onRetry: () => void - called when user clicks "Retry" after error
 * - onExit: () => void - called when user cancels/exits
 * - canAbort: boolean - whether the request can be aborted
 * - errorMessage: string - custom error message (optional)
 */
export default function MiniGameLoadingOverlay({
  loadStatus = 'loading',
  contentType = 'flashcards',
  onReady,
  onRetry,
  onExit,
  canAbort = false,
  errorMessage = "Couldn't load. Check connection.",
  isCompact = false,
  stageText = null,
}) {
  // Three-state overlay system
  const [overlayView, setOverlayView] = useState('spinner'); // 'spinner' | 'game'
  const [selectedGame, setSelectedGame] = useState(null);
  const [currentScore, setCurrentScore] = useState(0);
  const [showFinalScore, setShowFinalScore] = useState(false);
  const [finalScoreData, setFinalScoreData] = useState(null);
  const [showReadyNotice, setShowReadyNotice] = useState(false);
  
  // Derived state
  const contentStatus = loadStatus; // 'loading' | 'ready' | 'error'

  // Stable ref for onReady to avoid resetting auto-dismiss timer on re-renders
  const onReadyRef = useRef(onReady);
  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);

  // High score is managed internally by saveGameScore utility
  // No need to track in component state since it's not displayed

  // Auto-dismiss overlay when content is ready and user is on spinner view
  useEffect(() => {
    if (contentStatus === 'ready' && overlayView === 'spinner') {
      const timer = setTimeout(() => {
        if (onReadyRef.current) onReadyRef.current();
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [contentStatus, overlayView]);

  // Watch for content becoming ready while in game
  useEffect(() => {
    if (contentStatus === 'ready' && overlayView === 'game') {
      setShowReadyNotice(true);
    }
  }, [contentStatus, overlayView]);

  // Handle game selection
  const handleGameSelect = (gameId) => {
    setSelectedGame(gameId);
    setOverlayView('game');
    setCurrentScore(0);
    setShowFinalScore(false);
    setShowReadyNotice(false);
  };

  // Handle score changes from game
  const handleScoreChange = (newScore) => {
    setCurrentScore(newScore);
  };

  // Exit game — if content is ready, close overlay; otherwise return to spinner view
  const handleExitGame = () => {
    finalizeScore();
    // If content is already ready, dismiss the overlay immediately
    if (contentStatus === 'ready') {
      if (onReady) onReady();
      return;
    }
    setOverlayView('spinner');
    setSelectedGame(null);
    setShowReadyNotice(false);
  };

  // Start Now - the ONLY way to close the overlay
  const handleStartNow = () => {
    finalizeScore();
    if (onReady) {
      onReady();
    }
    // This closes the overlay by calling parent's onReady
  };

  // Handle Retry button click
  const handleRetry = () => {
    // Reset to spinner view
    setOverlayView('spinner');
    setSelectedGame(null);
    setCurrentScore(0);
    setShowFinalScore(false);
    setShowReadyNotice(false);
    if (onRetry) {
      onRetry();
    }
  };

  // Handle close overlay action (from Cancel in spinner view)
  const handleCloseOverlay = () => {
    finalizeScore();
    if (onExit) {
      onExit();
    }
  };

  // Finalize and save score
  const finalizeScore = () => {
    if (overlayView === 'game' && currentScore > 0) {
      const result = saveGameScore(contentType, currentScore);
      setFinalScoreData({
        finalScore: currentScore,
        previousBest: result.previousBest,
        isNewHighScore: result.isNewHighScore,
      });
      setShowFinalScore(true);
      
      // Update high score display if new record
      // High score is already saved by saveGameScore utility
    }
  };

  const isGameActive = overlayView === 'game';

  // Content type display names
  const contentTypeNames = {
    flashcards: 'Flashcards',
    quizzes: 'Quiz',
    podcasts: 'Podcast',
    audiobooks: 'Audiobook',
  };

  return (
    <div className={`minigame-loading-overlay ${isCompact ? 'minigame-loading-overlay--compact' : ''}`}>
      <div className="overlay-shell">
        {/* SPINNER VIEW */}
        {overlayView === 'spinner' && (
          <>
            <div className="prompt-content">
              <div className="spinner-block">
                {/* LOADING STATE */}
                {contentStatus === 'loading' && (
                  <>
                    <div className="loading-spinner" />
                    <p className="loading-title">Generating {contentTypeNames[contentType]}…</p>
                    {stageText && <p className="loading-stage-text">{stageText}</p>}
                    <p className="prompt-line-1">Play a quick game while you wait?</p>
                    <p className="prompt-line-2">Pick one below</p>
                    <p className="prompt-line-3">💡 You can leave this page and return anytime. We'll notify you when it's ready.</p>
                  </>
                )}
                
                {/* READY STATE */}
                {contentStatus === 'ready' && (
                  <>
                    <div className="ready-checkmark">✅</div>
                    <p className="loading-title">{contentTypeNames[contentType]} is ready!</p>
                    <button className="btn-ready-prompt" onClick={handleStartNow}>
                      Start Now
                    </button>
                    <p className="prompt-line-2 ready-subtext">Or pick a game to play anyway</p>
                  </>
                )}
                
                {/* ERROR STATE */}
                {contentStatus === 'error' && (
                  <div className="error-prompt">
                    <div className="error-icon">⚠️</div>
                    <p className="error-message">{errorMessage}</p>
                    <div className="error-actions">
                      <button className="btn-retry" onClick={handleRetry}>
                        Retry
                      </button>
                      <button className="btn-exit-secondary" onClick={handleCloseOverlay}>
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Game selector always visible in spinner view */}
            <LoadingGameSelector
              selectedGame={selectedGame}
              onSelect={handleGameSelect}
              disabledGames={['runner', 'taptojump', 'whack']}
            />
          </>
        )}

        {/* GAME VIEW */}
        {overlayView === 'game' && (
          <>
            {/* Full Panel Game - fills entire overlay */}
            <div className={`game-stage-full ${contentStatus === 'error' ? 'dimmed' : ''}`}>
              {selectedGame === 'sidetosidecatcher' && (
                <SideToSideCatcherGame
                  isActive={isGameActive}
                  onScoreChange={handleScoreChange}
                  currentScore={currentScore}
                  onExit={handleExitGame}
                  showFinalScore={showFinalScore}
                  finalScoreData={finalScoreData}
                  isCompact={isCompact}
                  contentReady={contentStatus === 'ready'}
                  contentTypeName={contentTypeNames[contentType]}
                  onViewContent={handleStartNow}
                />
              )}
              {selectedGame === 'dodge' && (
                <DodgeSurvivalGame
                  isActive={isGameActive}
                  onScoreChange={handleScoreChange}
                  currentScore={currentScore}
                  onExit={handleExitGame}
                  showFinalScore={showFinalScore}
                  finalScoreData={finalScoreData}
                  isCompact={isCompact}
                  contentReady={contentStatus === 'ready'}
                  contentTypeName={contentTypeNames[contentType]}
                  onViewContent={handleStartNow}
                />
              )}
            </div>

            {/* Ready Notice Pill (top-center, doesn't block gameplay) */}
            {showReadyNotice && contentStatus === 'ready' && (
              <div className="ready-notice-bar">
                <div className="ready-notice-content">
                  <span className="ready-notice-text">
                    ✅ {contentTypeNames[contentType]} ready!
                  </span>
                  <div className="ready-notice-actions">
                    <button className="btn-notice-start" onClick={handleStartNow}>
                      View
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Error Notice Bar (below game stage) */}
            {contentStatus === 'error' && (
              <div className="error-notice-bar">
                <div className="error-notice-content">
                  <span className="error-notice-text">⚠️ {errorMessage}</span>
                  <div className="error-notice-actions">
                    <button className="btn-notice-retry" onClick={handleRetry}>
                      Retry
                    </button>
                    <button className="btn-notice-exit" onClick={handleExitGame}>
                      Exit Game
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
